import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1800;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

const Stats = () => {
  const { t } = useLanguage();

  const statsList = [
    { value: 50, suffix: "+", label: t("stats.s1Label") },
    { value: 6, suffix: "", label: t("stats.s2Label") },
    { value: 500, suffix: "+", label: t("stats.s3Label") },
    { value: 10, suffix: "K+", label: t("stats.s4Label") },
  ];

  return (
    <section className="relative py-24 md:py-32 overflow-hidden" data-testid="stats-section">
      <img src={IMAGES.paddy} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      {/* light green wash keeps text crisp, never dark */}
      <div className="absolute inset-0 bg-[#2d6a4f]/88" />
      <div className="absolute inset-0 grain-overlay opacity-[0.06]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-[#d4af37]" />
            <span className="eyebrow text-[#d8f3dc]">{t("stats.eyebrow")}</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mt-5 leading-tight">
            {t("stats.title")} {t("stats.titleHighlight")}
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 mt-16">
          {statsList.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`stat-${i}`}
            >
              <p className="font-display text-5xl md:text-6xl font-bold text-white tracking-tight">
                <Counter value={s.value} suffix={s.suffix} />
              </p>
              <div className="h-px w-10 bg-[#d4af37] my-4" />
              <p className="font-body text-sm text-[#d8f3dc] leading-snug">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
